import React, {Component} from 'react';
import PropTypes from 'prop-types';

import Paper from 'material-ui/Paper';
import Badge from 'material-ui/Badge';
import { Tabs, Tab } from 'material-ui/Tabs';

import PageHeading from '../../components/PageHeading';
import EditUserAccountDialog from '../../components/EditUserAccountDialog';
import UserTable from '../../components/UserTable';
import LoaderOverlay from '../../components/LoaderOverlay';
import { confirmDialog, notifyBar } from '../../util/dialogs';
import UserStore from '../../util/UserStore';

/**
 * Helper to hide the 'unassigned user' badge if the count is zero
 * @param {String} content
 * @param {Number} badgeCount
 * @param {Boolean} showIfZero show badge if count is 0
 */
function withBadge(content, badgeCount, showIfZero = false) {
  if (!showIfZero && !badgeCount) {
    return content;
  }

  return (
    <Badge
      badgeContent={badgeCount}
      secondary={true}
    >
      {content}
    </Badge>
  );
}

const styles = {
  userAdminPage: {
    marginBottom: 15,
  },
}

/**
 * Container for user administration page. shows a page with 3 tabs:
 * - unassigned users
 * - (regular) users
 * - help
 */
class UserAdmin extends Component {
  static contextTypes = {
    firebaseToken: PropTypes.string.isRequired,
  }

  constructor(props, context) {
    super(props, context);
    this.state = {
      loading: true,
      initialLoadCompleted: false,
      users: [],
    };
    this.userStore = new UserStore(context.firebaseToken);
    this.userStore.onListUpdate = this.handleListUpdate;
    this.userStore.onLoadStart = this.handleLoadStart;
    this.userStore.onLoadComplete = this.handleLoadComplete;
  }

  componentDidMount() {
    this.userStore.refreshUserList();
  }

  handleLoadStart = () => {
    this.setState({
      loading: true,
    });
  }

  handleLoadComplete = () => {
    this.setState({
      loading: false,
    });
  }

  handleListUpdate = (data) => {
    this.setState({
      users: data,
      initialLoadCompleted: true,
    });
  }
  handleCreateUserShow = () => {
    this.setState({
      showCreateForm: true,
    })
  }

  handleCreateUserClose = () => {
    this.setState({
      showCreateForm: false,
    })
  }

  handleCreateUserSave = (newUser) => {
    // Notify when the new user email is already taken by another user.
    const {users} = this.state;
    let emailExists = users.filter(user => user.email === newUser.email).length !== 0;
    if (emailExists) {
      notifyBar(`User with email '${newUser.email}' already exists!`);
      return;
    }
    // Do stuff to create the user here.
    this.handleCreateUserClose();

    confirmDialog(`Send out an automated welcome mail to ${newUser.email}?`)
      .then(shouldMail => {

        this.userStore.create(newUser, shouldMail)
          .then(() => {
            // TODO: This also executes when a network error occurred... fix.
            notifyBar('User created.');
          });
      });
  }

  render() {
    const { users, loading, initialLoadCompleted,} = this.state;
    const unAssignedUsers = users.filter(user => user.email && user.email.match(/screen6.io$/) === null && !user.client);
    const normalUsers = users.filter(user => user.email && user.client);

    const amountOfUnassigned = unAssignedUsers.length;

    let userAdminTables = false;
    if (initialLoadCompleted) {
      userAdminTables = (
        <Paper>
          <Tabs>
            <Tab
              label={
                withBadge('Unassigned users', amountOfUnassigned)
              }
            >
              <UserTable showClientColumn={false} showTopBar={false} users={unAssignedUsers}
                userStore={this.userStore} 
              />
            </Tab>
            <Tab label="Users" >
              <UserTable showTopBar={true} users={normalUsers} userStore={this.userStore}
                onAddUserClicked={this.handleCreateUserShow}
              />
              {this.state.showCreateForm &&
                <EditUserAccountDialog user={{}}
                  onRequestClose={this.handleCreateUserClose}
                  onSave={this.handleCreateUserSave} 
                />
              }
            </Tab>
            <Tab label="Help" >
                <div className="ContentSpacing">
                  <h3>Unassigned users</h3>
                  <p>
                    This section holds users that have not been assigned to a client yet. These are usually users that
                    have tried to sign up to the hub themselves.
                  </p>
                  <p>
                    After verifying their identity, assign them to a client by choosing 'Edit account' from the
                    user&apos;s context menu. You can now select a client from the droplist.
                  </p>
                  <p>
                    After saving the changes you&apos;ll be prompted to send the user a confirmation mail. This will be
                    asked once per unassigned user.
                  </p>
                  <hr/>
                  <h3>Users</h3>
                  <p>
                    This section holds all users that are assigned to a client. Here you can edit, disable or delete a
                    client using the context menu.
                  </p>
                  <h4>Creating a user</h4>
                  <p>
                    Click 'ADD USER' (in the table header) to set up a new user. The field `display name` is optional.
                    After saving your changes you will be prompted to send the new user a welcome mail. This is the only
                    point in time where you can have this automated mail sent. We will add a separate button for it if
                    this is desirable.
                  </p>
                  <h4>Disabling a user</h4>
                  <p>
                    By disabling a user you can (temporarily) revoke a user&apos;s access to The Hub.
                  </p>
                  <h4>Deleting a user</h4>
                  <p>
                    By disabling a user you can revoke a user&apos;s access to The Hub.
                  </p>
                  <p>
                    After saving the changes you&apos;ll be prompted to send the user a confirmation mail. This will be
                    asked once per unassigned user.
                  </p>
                  <hr/>
                  <h3>Adding new clients</h3>
                  <p>
                    Clients currently cannot be managed from this interface. For the time being Ops will take care of
                    this on request.
                  </p>
                  <p>For Ops:</p>
                  <ul>
                    <li>
                      Visit the <a href="https://console.firebase.google.com/u/0/project/screen6-portal/database/data/s6profiles/clients">Firebase DB Console</a> to
                      manage clients.
                    </li>
                    <li>
                      Each client should have an access profile containing ACL (access control) and one or more
                      datasets.
                    </li>
                    <li>
                      Every item in the default ACL is set to false*, so you only need to add those that the client
                      needs access to.
                      (* = except admins, they have access to everything by default)
                    </li>
                    <li>
                      Currently valid ACL items: `amp`, `dashboard`, `householding` and `poi`. The rest is not implemented (yet).
                    </li>
                  </ul>
                </div>
            </Tab>
          </Tabs>
        </Paper>
      )
    }
    return (
      <div style={styles.userAdminPage}>
        <PageHeading title={"User Management"} />
        <LoaderOverlay
          visible={loading}
        />
        {userAdminTables}
      </div>
    );
  }
}

export default UserAdmin;



// WEBPACK FOOTER //
// ./src/containers/UserAdmin/index.jsx